import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet, apiPost } from "../../shared/apiClient";
import { deleteState, fetchStatesList } from "../../shared/geographyMastersApi";
import { FormModalShell } from "../../shared/FormModalShell";
import { IconClose, IconPlus, IconSave } from "../../shared/toolbarIcons";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  height: "100%",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f2fcf5",
    borderBottom: "1px solid #e8ecea",
    fontWeight: 600
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e8ecea",
    backgroundColor: "#fbfffc"
  },
  "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
    backgroundColor: "#fcfffd"
  },
  "& .MuiDataGrid-cell": {
    borderBottomColor: "#e8ecea"
  }
};

const emptyForm = () => ({
  countryId: "",
  name: "",
  isActive: true
});

export function StatesList() {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());
  const [formError, setFormError] = useState(null);

  const countryById = useMemo(() => {
    const m = new Map();
    for (const c of countries) m.set(c.id, c);
    return m;
  }, [countries]);

  const rows = useMemo(() => {
    return items.map((s) => {
      const country = countryById.get(s.countryId);
      return {
        id: s.id,
        state: s,
        countryName: country?.name ?? "-"
      };
    });
  }, [items, countryById]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [sRes, cRes] = await Promise.all([fetchStatesList(), apiGet("/api/Countries")]);
    if (!sRes.ok) {
      setError(sRes.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(sRes.items);
    }
    if (cRes.ok) setCountries(cRes.data ?? []);
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this state?"))) return;
      const res = await deleteState(id);
      if (!res.ok) setError(res.error ?? "Failed to delete state");
      else {
        if (editingId === id) {
          setIsModalOpen(false);
          setEditingId(null);
          setForm(emptyForm());
          setFormError(null);
        }
        await load();
      }
    },
    [editingId, load]
  );

  const openCreateModal = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback((state) => {
    setEditingId(state.id);
    setForm({
      countryId: state.countryId ?? "",
      name: state.name ?? "",
      isActive: Boolean(state.isActive)
    });
    setFormError(null);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.countryId) {
      setFormError("Country and state name are required.");
      return;
    }

    setSaving(true);
    setFormError(null);

    const res = await apiPost("/api/States", {
      id: editingId ?? 0,
      name: form.name.trim(),
      isActive: form.isActive,
      countryId: Number(form.countryId)
    });

    if (!res.ok) {
      setFormError(res.error ?? "Failed to save state");
      setSaving(false);
      return;
    }

    closeModal();
    await load();
    setSaving(false);
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "State",
        flex: 1,
        minWidth: 140,
        valueGetter: (_v, row) => row.state.name ?? "-"
      },
      {
        field: "countryName",
        headerName: "Country",
        flex: 1,
        minWidth: 140,
        valueGetter: (_v, row) => row.countryName
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const s = params.row.state;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="Edit" onClick={() => openEditModal(s)}>
                ✏️
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(s.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [handleDeleteClick, openEditModal]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>States</h2>
          <p>{loading ? "Loading states..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <button className="icon-button icon-button--toolbar" type="button" title="Add state" aria-label="Add state" onClick={openCreateModal}>
            <IconPlus />
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={dataGridSx}
          />
        </div>
      </div>
      <FormModalShell
        open={isModalOpen}
        title={editingId ? "Edit State" : "Add State"}
        subtitle="Manage state details."
        onClose={closeModal}
        actions={
          <>
            <button
              type="button"
              className="icon-button icon-button--toolbar"
              onClick={closeModal}
              disabled={saving}
              title="Cancel"
              aria-label="Cancel"
            >
              <IconClose />
            </button>
            <button
              type="submit"
              form="state-modal-form"
              className="icon-button icon-button--toolbar"
              disabled={saving}
              title={saving ? "Saving" : "Save"}
              aria-label={saving ? "Saving" : "Save"}
            >
              <IconSave />
            </button>
          </>
        }
      >
        <form id="state-modal-form" onSubmit={handleSubmit} className="form form-fields-bottomline">
          <div className="form-row form-row--4">
            <label className="field">
              <span>Country</span>
              <select value={form.countryId} onChange={(event) => setForm((prev) => ({ ...prev, countryId: event.target.value ? Number(event.target.value) : "" }))}>
                <option value="">Select country</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>State name</span>
              <input type="text" value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            </label>
          </div>
          {formError && <div className="error-banner">{formError}</div>}
        </form>
      </FormModalShell>
    </div>
  );
}
